import { computed, onMounted, ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { useRoute } from 'vue-router';

import { useService } from '@nzyme/vue-ioc/useService.js';
import { useLocale } from '@sigma/client/i18n/useLocale.js';
import { LandingRoute } from '@sigma/patient-common/routes.js';

import { MarketingParamsStore } from './MarketingParamsStore.js';

/**
 * Build a reactive landing-route location that carries `utm_campaign` forward, so the campaign — and thus
 * the served landing variant — survives navigating away from the landing page and back, refresh, and
 * copy-URL / open-in-new-tab (which a route guard can't cover, since they use the rendered `href`).
 *
 * The campaign is taken from the current URL when present, otherwise from the sessionStorage capture
 * (read after mount, so the prerendered/SSR `href` stays param-less and hydration is clean — the link is
 * patched reactively once interactive).
 *
 * Use this for every internal link that navigates to the landing page.
 */
export function useLandingRoute() {
    const route = useRoute();
    const locale = useLocale();
    const marketingParams = useService(MarketingParamsStore);
    const storedUtmCampaign = ref<string | undefined>(undefined);

    onMounted(() => {
        storedUtmCampaign.value = marketingParams.params.utm_campaign;
    });

    return computed<RouteLocationRaw>(() => {
        const fromQuery = typeof route.query.utm_campaign === 'string' ? route.query.utm_campaign : undefined;
        const utmCampaign = fromQuery ?? storedUtmCampaign.value;

        return LandingRoute({
            locale: locale.value,
            $query: utmCampaign ? { utm_campaign: utmCampaign } : undefined,
        });
    });
}
